import * as XLSX from "xlsx";
import { defaultSysCodeDto, SysCodeDto } from "src/dtos/code/sysCodeDto";
import { HRM3130_R01TaxTableDto, HRM3130_R01SurchargeRuleDto, defaultHRM3130_R01TaxTableDto } from "./Dto_HRM3130_R01";
/** 가족별 세액 필드 순서이며 상수 배열의 리터럴 타입을 유지한다. */
export const HRM3130_R01_TAX_FIELDS = ["tax1", "tax2", "tax3", "tax4", "tax5", "tax6", "tax7", "tax8", "tax9", "tax10", "tax11"] as const;
export const HRM3130_R01_TEN_MILLION = 10000000; // 표의 가산 기준 행 시작 금액
/** 공통 드롭다운이 인식하는 코드 DTO 모양으로 만든 화면 고정 가족수 목록이다. */
export const HRM3130_R01_FAMILY_COUNT_LIST: SysCodeDto[] = Array.from({ length: 11 }, (_, i) => ({
    ...defaultSysCodeDto, majorCd: 'FAMILY_COUNT', minorCd: String(i + 1), codeNm: `${i + 1}명`,
}));
/** 실제 DTO 필드에 대응하는 컬럼 제목이다. */
export const headerNames_HRM3130_R01: Record<string, string> = {
    rowIndex: '', fromAmt: '월급여 이상', toAmt: '미만',
    tax1: '1명', tax2: '2명', tax3: '3명', tax4: '4명', tax5: '5명', tax6: '6명', tax7: '7명', tax8: '8명', tax9: '9명', tax10: '10명', tax11: '11명', remark: '비고',
};
/** 금액을 천 단위 구분 문자열로 반환한다. 건수 문자열은 그대로 반환한다. */
export const formatHRM3130_R01Amount = (value: number | string | null | undefined): string =>
    typeof value === 'number' ? value.toLocaleString('ko-KR', { maximumFractionDigits: 0 }) : value ?? '';
/** 정렬된 전체 목록과 연도를 검사하고 첫 오류 문장 또는 null을 반환한다. */
export const validateHRM3130_R01SaveData = (rows: HRM3130_R01TaxTableDto[], applyYear: string): string | null => {
    if (!rows.length) return '저장할 간이세액표가 없습니다.';
    for (const [i, row] of Array.from(rows.entries())) {
        const amount = formatHRM3130_R01Amount(row.fromAmt); // 오류에 표시할 시작 금액
        const previous = rows[i - 1]; // 앞 구간, 첫 행이면 undefined
        if (row.applyYear !== applyYear) return '적용년도가 다른 행이 있습니다: ' + amount;
        if (row.fromAmt == null || row.toAmt == null || !Number.isFinite(row.fromAmt) || !Number.isFinite(row.toAmt) || row.fromAmt < 0 || row.toAmt < 0) return '금액을 확인해주세요: ' + amount;
        if (row.toAmt <= row.fromAmt) return '미만 금액은 이상 금액보다 커야 합니다: ' + amount;
        if (previous && previous.fromAmt === row.fromAmt) return '월급여 이상 금액이 중복됩니다: ' + amount;
        if (previous && previous.toAmt !== row.fromAmt) return '구간이 이어지지 않습니다: ' + amount;
        if (HRM3130_R01_TAX_FIELDS.some(field => row[field] == null || !Number.isFinite(row[field]) || row[field] < 0)) return '세액을 확인해주세요: ' + amount;
        if (row.remark && row.remark.length > 1000) return '비고는 1000자 이하로 입력해주세요: ' + amount;
    }
    return rows.some(row => row.fromAmt === HRM3130_R01_TEN_MILLION) ? null : '1천만원 행이 없습니다.';
};
/** 그리드 교체 전 사용자가 원본과 비교할 업로드 요약이다. */
export interface HRM3130_R01UploadSummary {
    applyYear: string; // 대상 연도
    effectiveDate: string; // 파일명에서 읽은 시행일
    rowCount: number; // 전체 행 수
    unit: string; // 급여 단위 변환
    firstRow: HRM3130_R01TaxTableDto; // 첫 구간
    representativeRow: HRM3130_R01TaxTableDto | undefined; // 300만원 구간 바로 앞 행
    tenMillionRow: HRM3130_R01TaxTableDto; // 1천만원 기준 행
    isSurchargeRuleMatched: boolean; // true이면 구간 수와 모든 값이 일치
    surchargeDifferences: string[]; // 구간별 서버·엑셀 차이
    isSurchargeRuleAvailable: boolean; // true이면 서버 산식 조회에 성공
}
/** FileReader로 엑셀 바이너리를 읽어 반환한다. 실패하면 오류를 전달한다. */
const readHRM3130_R01File = (file: File): Promise<ArrayBuffer> => new Promise((resolve, reject) => {
    const reader = new FileReader(); // 선택 파일 읽기 전용
    reader.onload = () => reader.result instanceof ArrayBuffer ? resolve(reader.result) : reject(new Error('엑셀 파일을 읽을 수 없습니다.'));
    reader.onerror = () => reject(new Error('엑셀 파일을 읽을 수 없습니다.'));
    reader.readAsArrayBuffer(file);
});
/** 모든 시트에서 내용으로 표 위치를 찾고 검증된 행과 요약을 반환한다. 실패하면 그리드를 변경하지 않는다. */
export const parseHRM3130_R01NtsExcel = async (file: File, applyYear: string, surchargeRuleList: HRM3130_R01SurchargeRuleDto[] | undefined): Promise<{ rowList: HRM3130_R01TaxTableDto[]; summary: HRM3130_R01UploadSummary }> => {
    const workbook = XLSX.read(await readHRM3130_R01File(file), { type: 'array' }); // 바이너리 통합 문서
    const candidates = workbook.SheetNames.flatMap(name => {
        const rows = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[name], { header: 1, defval: null }); // 실제 셀의 2차원 목록
        return rows.flatMap((row, h) => {
            const labels = row.map(cell => String(cell ?? '').trim()); // 공백을 제거한 제목
            return labels.includes('이상') && labels.includes('미만') ? [{ rows, h, fromCol: labels.indexOf('이상'), toCol: labels.indexOf('미만') }] : [];
        });
    });
    if (!candidates.length) throw new Error('간이세액표 시트를 찾을 수 없습니다.');
    if (candidates.length !== 1) throw new Error('간이세액표 시트가 여러 개입니다.');
    const { rows, h, fromCol, toCol } = candidates[0]; // 데이터와 제목 좌표
    const above = rows.slice(Math.max(0, h - 3), h); // 제목 위 최대 3행
    const familyHeader = above.find(row => Array.from({ length: 11 }, (_, i) => i + 1).every(n => row.filter(cell => Number(cell) === n).length === 1)); // 가족수 1~11이 유일한 행
    if (!familyHeader) throw new Error('공제대상가족수(1~11) 제목을 찾을 수 없습니다.');
    const taxColumns = HRM3130_R01_TAX_FIELDS.map((_, i) => familyHeader.findIndex(cell => Number(cell) === i + 1)); // 실제 가족수 열
    const unitLabel = above.map(row => String(row[fromCol] ?? '')).join(' '); // 이상 열의 급여 단위
    const multiplier = unitLabel.includes('(천원)') ? 1000 : unitLabel.includes('(원)') ? 1 : 0; // 명시된 단위만 적용
    if (!multiplier) throw new Error('월급여액 단위를 찾을 수 없습니다.');
    const rowList: HRM3130_R01TaxTableDto[] = []; // 검증 전 변환 결과
    const parsedRules: (HRM3130_R01SurchargeRuleDto | undefined)[] = []; // 읽지 못한 산식도 불일치로 보존
    let generalCount = 0; // 일반 구간 개수
    let readingRules = false; // true이면 표 행 뒤의 산식을 읽는 중
    for (let i = h + 1; i < rows.length; i++) {
        const cells = rows[i]; // 현재 엑셀 행
        const from = cells[fromCol]; // 이상 셀 원본
        const to = cells[toCol]; // 미만 셀 원본
        const text = String(from ?? '').trim(); // 구간 문구
        // 숫자와 천원만으로 구성된 기준 행을 찾는다.
        const special = text.match(/^\s*([\d,]+)\s*천원\s*$/);
        // 산식 구간의 초과 금액을 찾는다.
        const over = text.match(/([\d,]+)\s*천원\s*초과/);
        if (over) {
            readingRules = true;
            const formula = cells.map(cell => String(cell ?? '')).join(' '); // 같은 행 전체 산식 문장
            // 괄호 안의 고정 가산액을 찾는다.
            const add = formula.match(/\(\s*([\d,]+)\s*원\s*\)/);
            // 상당액 바로 앞의 세율을 찾는다.
            const rate = formula.match(/(\d+(?:\.\d+)?)\s*%\s*상당액/);
            // 쉼표를 제거해 금액 숫자로 변환한다.
            parsedRules.push(add && rate ? { overAmt: Number(over[1].replace(/,/g, '')) * 1000, addAmt: Number(add[1].replace(/,/g, '')), taxRate: Number(rate[1]), isApplyNinetyEightPercent: formula.includes('98%를 곱한') } : undefined);
            continue;
        }
        // 산식 사이의 상한 문구는 데이터가 아니다.
        if (/천원\s*이하/.test(text)) continue;
        const general = typeof from === 'number' && typeof to === 'number'; // true이면 일반 금액 구간
        if (!general && !(special && (to == null || String(to).trim() === ''))) {
            if (readingRules || (rowList.length > 0 && cells.every(cell => cell == null || String(cell).trim() === ''))) break;
            throw new Error(`국세청 간이세액표 형식이 아닙니다: ${i + 1}행`);
        }
        const fromAmt = general ? Number(from) * multiplier : Number(special![1].replace(/,/g, '')) * 1000; // 원 단위 시작
        if (!general && fromAmt !== HRM3130_R01_TEN_MILLION) throw new Error(`국세청 간이세액표 형식이 아닙니다: ${i + 1}행`);
        const row: HRM3130_R01TaxTableDto = { ...defaultHRM3130_R01TaxTableDto, applyYear, fromAmt, toAmt: general ? Number(to) * multiplier : fromAmt + 1, inserted: true }; // 신규 변환 행
        for (const [k, field] of Array.from(HRM3130_R01_TAX_FIELDS.entries())) {
            const value = cells[taxColumns[k]]; // 가족수 열의 실제 세액
            if (typeof value === 'number') row[field] = value;
            else if (String(value ?? '').trim() === '-') row[field] = 0;
            else throw new Error(`세액 값을 읽을 수 없습니다: ${i + 1}행 ${k + 1}명`);
        }
        if (general) generalCount++;
        else row.remark = '1천만원 초과는 가산 산식 적용';
        rowList.push(row);
    }
    const error = validateHRM3130_R01SaveData(rowList, applyYear); // 저장과 동일한 검증
    if (error) throw new Error(error);
    if (generalCount < 100) throw new Error(`읽은 구간이 너무 적습니다: ${generalCount}건`);
    if (rowList[0].fromAmt < 100000 || rowList[0].fromAmt >= HRM3130_R01_TEN_MILLION) throw new Error(`월급여액 단위가 맞지 않습니다: 첫 구간 ${formatHRM3130_R01Amount(rowList[0].fromAmt)}원`);
    for (const [i, row] of Array.from(rowList.entries())) {
        for (const [k, field] of Array.from(HRM3130_R01_TAX_FIELDS.entries())) {
            if (i > 0 && row[field] < rowList[i - 1][field]) throw new Error(`급여가 높은데 세액이 더 적습니다: ${formatHRM3130_R01Amount(row.fromAmt)}원 구간 ${k + 1}명`);
            if (k > 0 && row[field] > row[HRM3130_R01_TAX_FIELDS[k - 1]]) throw new Error(`가족이 많은데 세액이 더 많습니다: ${formatHRM3130_R01Amount(row.fromAmt)}원 구간 ${k + 1}명`);
        }
    }
    const differences: string[] = []; // 산식 차이 상세
    if (surchargeRuleList) {
        if (parsedRules.length !== surchargeRuleList.length) differences.push(`구간 수: 서버 ${surchargeRuleList.length}, 엑셀 ${parsedRules.length}`);
        for (let i = 0; i < Math.max(parsedRules.length, surchargeRuleList.length); i++) {
            const actual = parsedRules[i]; // 엑셀 산식
            const expected = surchargeRuleList[i]; // 서버 산식
            if (!actual || !expected || actual.overAmt !== expected.overAmt || actual.addAmt !== expected.addAmt || actual.taxRate !== expected.taxRate || actual.isApplyNinetyEightPercent !== expected.isApplyNinetyEightPercent) {
                differences.push(`${i + 1}구간: 서버 ${expected ? JSON.stringify(expected) : '없음'}, 엑셀 ${actual ? JSON.stringify(actual) : '읽기 실패'}`);
            }
        }
    }
    // 파일명에서 YYYY.MM.DD 시행일을 찾는다.
    const effectiveDate = file.name.match(/(\d{4})\.(\d{2})\.(\d{2})/)?.[0] ?? '';
    if (effectiveDate) rowList[0].remark = effectiveDate + ' 시행';
    const tenMillionRow = rowList.find(row => row.fromAmt === HRM3130_R01_TEN_MILLION)!; // 검증된 기준 행
    const representativeRow = rowList[rowList.findIndex(row => row.fromAmt <= 3000000 && row.toAmt > 3000000) - 1]; // 300만원 구간 앞 행
    return { rowList, summary: { applyYear, effectiveDate, rowCount: rowList.length, unit: multiplier === 1000 ? '천원 → 원 변환' : '원', firstRow: rowList[0], representativeRow, tenMillionRow, isSurchargeRuleMatched: !!surchargeRuleList && differences.length === 0, surchargeDifferences: differences, isSurchargeRuleAvailable: !!surchargeRuleList } };
};

